import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  AlertCircle,
  Store,
  MapPin,
  Clock,
  Truck,
  BarChart3,
  Shield,
  Globe,
  Pencil,
} from 'lucide-react';
import { Button } from '../../ui/button';
import {
  OnboardingFormData,
  ONBOARDING_STEPS,
  DAYS_OF_WEEK,
  DayOfWeek,
} from '../../../types/business';
import { cn } from '../../../lib/utils';
import { formatTime12h } from '../../../utils/time';

interface StepReviewProps {
  formData: OnboardingFormData;
  stepCompletion: Array<{ step: number; isComplete: boolean; errors: string[] }>;
  onGoToStep: (step: number) => void;
  onActivate: () => Promise<boolean>;
  saving: boolean;
  error: string | null;
}

const DAY_LABELS: Record<DayOfWeek, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

const RETURN_LABELS: Record<string, string> = {
  full_refund: 'Full Refund',
  exchange_only: 'Exchange Only',
  store_credit: 'Store Credit',
  no_returns: 'All Sales Final',
  custom: 'Custom Policy',
};

export function StepReview({
  formData,
  stepCompletion,
  onGoToStep,
  onActivate,
  saving,
  error,
}: StepReviewProps) {
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const allComplete = stepCompletion.slice(0, 7).every(s => s.isComplete);
  const requiredComplete = stepCompletion.slice(0, 6).every(s => s.isComplete);

  const sections = [
    {
      step: 1,
      icon: <Store className="h-5 w-5" />,
      title: 'Business Identity',
      content: (
        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            {formData.logoPreview && (
              <img
                src={formData.logoPreview}
                alt="Logo"
                className="w-10 h-10 rounded-full object-cover"
              />
            )}
            <div>
              <p className="text-sm font-medium text-heading">{formData.name || '—'}</p>
              <p className="text-xs text-body line-clamp-2">{formData.description || '—'}</p>
            </div>
          </div>
          {formData.categories.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {formData.categories.map(cat => (
                <span key={cat} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  {cat}
                </span>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      step: 2,
      icon: <MapPin className="h-5 w-5" />,
      title: 'Location',
      content: (
        <div className="space-y-1">
          <p className="text-sm text-heading">
            {[formData.address.street, formData.address.unit].filter(Boolean).join(', ')}
          </p>
          <p className="text-sm text-body">
            {[formData.address.city, formData.address.state, formData.address.zipCode]
              .filter(Boolean).join(', ')}
          </p>
          <p className="text-xs text-primary font-medium mt-1">
            Delivery via Uber Direct
          </p>
        </div>
      ),
    },
    {
      step: 3,
      icon: <Clock className="h-5 w-5" />,
      title: 'Hours',
      content: (
        <div className="space-y-1">
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
            {DAYS_OF_WEEK.map(day => {
              const hours = formData.hours[day];
              return (
                <div key={day} className="flex justify-between text-xs">
                  <span className="text-body font-medium">{DAY_LABELS[day]}</span>
                  <span className={hours ? 'text-heading' : 'text-gray-400'}>
                    {hours ? `${formatTime12h(hours.open)} – ${formatTime12h(hours.close)}` : 'Closed'}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-body mt-1">
            Prep time: ~{formData.prepTimeEstimate} min
          </p>
        </div>
      ),
    },
    {
      step: 4,
      icon: <Truck className="h-5 w-5" />,
      title: 'Courier Pickup',
      content: (
        <div className="space-y-1">
          <p className="text-sm text-heading">Uber Direct Couriers</p>
          <p className="text-xs text-body">
            Packaging: {formData.fulfillment.packaging.join(', ')}
          </p>
          {formData.fulfillment.maxOrdersPerHour && (
            <p className="text-xs text-body">
              Max {formData.fulfillment.maxOrdersPerHour} orders/hour
            </p>
          )}
          {formData.fulfillment.pickupInstructions && (
            <p className="text-xs text-body truncate">
              Notes: {formData.fulfillment.pickupInstructions}
            </p>
          )}
        </div>
      ),
    },
    {
      step: 5,
      icon: <BarChart3 className="h-5 w-5" />,
      title: 'Catalog',
      content: (
        <div className="space-y-1">
          <p className="text-sm text-heading">
            Price range: ${formData.priceRange.min} – ${formData.priceRange.max}
          </p>
          <p className="text-xs text-body">
            Low stock alert: {formData.inventorySettings.lowStockThreshold} units
          </p>
          {formData.collections.length > 0 && (
            <p className="text-xs text-body">
              Collections: {formData.collections.join(', ')}
            </p>
          )}
        </div>
      ),
    },
    {
      step: 6,
      icon: <Shield className="h-5 w-5" />,
      title: 'Legal & Payouts',
      content: (
        <div className="space-y-1">
          <p className="text-sm text-heading">{formData.legal.businessName || '—'}</p>
          <p className="text-xs text-body capitalize">
            {formData.legal.businessType.replace(/_/g, ' ')}
          </p>
          <p className="text-xs text-body">
            Returns: {RETURN_LABELS[formData.legal.returnPolicy.type]}{' '}
            {formData.legal.returnPolicy.type !== 'no_returns' &&
              `(${formData.legal.returnPolicy.windowDays} days)`}
          </p>
        </div>
      ),
    },
    {
      step: 7,
      icon: <Globe className="h-5 w-5" />,
      title: 'Social & Branding',
      content: (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div
              className="w-5 h-5 rounded-full border"
              style={{ backgroundColor: formData.brandColor }}
            />
            <span className="text-xs text-body font-mono">{formData.brandColor}</span>
          </div>
          {formData.socialMedia.instagram && (
            <p className="text-xs text-body">IG: {formData.socialMedia.instagram}</p>
          )}
          {formData.socialMedia.tiktok && (
            <p className="text-xs text-body">TikTok: {formData.socialMedia.tiktok}</p>
          )}
          {formData.socialMedia.website && (
            <p className="text-xs text-body truncate">Web: {formData.socialMedia.website}</p>
          )}
          {!formData.socialMedia.instagram && !formData.socialMedia.tiktok && !formData.socialMedia.website && (
            <p className="text-xs text-gray-400 italic">No social links added</p>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif font-semibold text-heading">
          Review & Launch
        </h2>
        <p className="text-body mt-1">
          Review your boutique details. Everything looks good? Let's go live.
        </p>
      </div>

      {/* Completion status */}
      {!requiredComplete && (
        <div className="bg-error/10 border border-error/20 rounded-card p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-error flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-heading">Some steps need attention</p>
            <p className="text-xs text-body mt-1">
              Complete all required steps before launching your boutique.
            </p>
          </div>
        </div>
      )}

      {/* Section cards */}
      <div className="space-y-3">
        {sections.map((section) => {
          const completion = stepCompletion[section.step - 1];
          return (
            <div
              key={section.step}
              className={cn(
                'rounded-xl border p-4 transition-colors',
                completion?.isComplete ? 'border-gray-100' : 'border-warning/30 bg-warning/5'
              )}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      'p-1.5 rounded-lg',
                      completion?.isComplete
                        ? 'bg-success/10 text-success'
                        : 'bg-warning/10 text-warning'
                    )}
                  >
                    {completion?.isComplete ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      section.icon
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-heading">{section.title}</h3>
                </div>
                <button
                  type="button"
                  onClick={() => onGoToStep(section.step)}
                  className="text-xs text-primary hover:text-primary-hover font-medium flex items-center gap-1"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              </div>
              {section.content}
            </div>
          );
        })}
      </div>

      {/* Terms agreement */}
      <label className="flex items-start space-x-3 cursor-pointer">
        <input
          type="checkbox"
          checked={agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
        <span className="text-sm text-body">
          I agree to the{' '}
          <Link
            to="/merchant-agreement"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            Fash Merchant Agreement
          </Link>
          {' '}and confirm all information is accurate.
        </span>
      </label>

      {/* Error display */}
      {error && (
        <div className="bg-error/10 text-error text-sm p-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Launch button */}
      <Button
        type="button"
        fullWidth
        size="lg"
        loading={saving}
        disabled={!requiredComplete || !agreedToTerms}
        onClick={onActivate}
        className="text-base"
      >
        Activate My Boutique
      </Button>

      <p className="text-xs text-body text-center">
        You can edit all details after launching from your business settings.
      </p>
    </div>
  );
}
